import React, { useMemo, useState } from 'react';
import { Card, CardBody } from 'reactstrap';
import { ArrowDownAZ, ArrowUpAZ, TrendingDown, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import DeltaIndicator from './DeltaIndicator';

// Generic Work Orders card. Header with sort toggle (transient — V1 saves
// no user pref); aggregate metric with delta; per-org rows rendered via the
// caller-supplied `renderRow`.

const ROW_COLORS = {
  onTime: '#10b981',     // green
  late: '#f59e0b',       // amber
  overdue: '#ef4444',    // red
  preventive: '#10b981', // green
  reactive: '#3b82f6',   // blue
  other: '#cbd5e1',      // light gray
};

// Per-org row: name + percentage + tiny trend arrow.
export const CompletedPctRow = ({ org }) => {
  const valueLabel = org.pct == null ? '—' : `${org.pct.toFixed(1)}%`;
  let arrow = null;
  if (org.trend === 'up') arrow = <TrendingUp size={14} style={{ color: ROW_COLORS.onTime }} />;
  else if (org.trend === 'down') arrow = <TrendingDown size={14} style={{ color: ROW_COLORS.overdue }} />;
  return (
    <div className="d-flex justify-content-between align-items-center py-1">
      <span className="text-truncate font-size-13" style={{ maxWidth: '60%' }}>
        {org.subOrgName}
      </span>
      <span className="d-flex align-items-center gap-2">
        <span className="font-size-13 fw-semibold">{valueLabel}</span>
        {arrow}
      </span>
    </div>
  );
};

// Per-org row: name + segmented colored bar. Segments are described by
// caller via `segments: [{ value, color }]`.
export const SegmentedBarRow = ({ org }) => {
  const total = org.segments.reduce((a, s) => a + (s.value || 0), 0);
  return (
    <div className="d-flex align-items-center gap-3 py-1">
      <span className="text-truncate font-size-13" style={{ width: '40%' }}>
        {org.subOrgName}
      </span>
      <div className="d-flex flex-grow-1" style={{ height: 10, borderRadius: 999, overflow: 'hidden', backgroundColor: '#f1f5f9' }}>
        {total === 0
          ? null
          : org.segments.map((s, i) => (
              <div
                key={i}
                style={{
                  width: `${(s.value / total) * 100}%`,
                  backgroundColor: s.color,
                }}
                title={`${s.label}: ${s.value}`}
              />
            ))}
      </div>
    </div>
  );
};

export { ROW_COLORS };

// Tiny legend below the per-org rows. Pass `legend: [{ color, label }]` to
// the parent card to render — kept inside the card so the swatch and the
// segments line up visually.
const Legend = ({ items }) => (
  <div className="d-flex flex-wrap gap-3 mt-3 pt-2 border-top">
    {items.map((i) => (
      <span
        key={i.label}
        className="d-inline-flex align-items-center gap-1 font-size-12 text-muted"
      >
        <span
          style={{
            width: 10,
            height: 10,
            borderRadius: 2,
            backgroundColor: i.color,
            display: 'inline-block',
          }}
        />
        {i.label}
      </span>
    ))}
  </div>
);

export default function WoBreakdownCard({
  title,
  subtitle,
  aggregatePct,
  aggregateDelta,
  betterIsUp = true,
  orgs,
  renderRow,
  sortValue, // (org) => numeric, for sorting
  legend, // optional [{ color, label }]
}) {
  const { t } = useTranslation();
  const [sortDir, setSortDir] = useState('asc'); // 'asc' = low to high

  const sortedOrgs = useMemo(() => {
    if (!orgs) return [];
    const copy = [...orgs];
    copy.sort((a, b) => {
      const av = sortValue ? sortValue(a) ?? -Infinity : 0;
      const bv = sortValue ? sortValue(b) ?? -Infinity : 0;
      return sortDir === 'asc' ? av - bv : bv - av;
    });
    return copy;
  }, [orgs, sortDir, sortValue]);

  const toggleSort = () => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
  const sortIcon = sortDir === 'asc' ? ArrowUpAZ : ArrowDownAZ;
  const SortIcon = sortIcon;
  const sortLabel = sortDir === 'asc'
    ? t('shared.global_org.overview.sort_low_to_high', 'Low to high')
    : t('shared.global_org.overview.sort_high_to_low', 'High to low');

  return (
    <Card className="h-100">
      <CardBody>
        <div className="d-flex justify-content-between align-items-start mb-2">
          <div className="fw-semibold">{title}</div>
          <button
            type="button"
            className="btn btn-sm btn-light d-flex align-items-center gap-1 py-0 px-2"
            onClick={toggleSort}
            style={{ fontSize: 12 }}
          >
            <SortIcon size={14} />
            {sortLabel}
          </button>
        </div>
        <div className="d-flex align-items-center gap-2 mb-1">
          <div className="font-size-28 fw-bold">
            {aggregatePct == null ? '—' : `${aggregatePct.toFixed(1)}%`}
          </div>
          <DeltaIndicator delta={aggregateDelta} betterIsUp={betterIsUp} />
        </div>
        <div className="text-muted font-size-12 mb-3">{subtitle}</div>
        <div>
          {sortedOrgs.length === 0 ? (
            <div className="text-muted font-size-12">
              {t('shared.global_org.overview.no_data_yet', 'No data yet')}
            </div>
          ) : (
            sortedOrgs.map((org) => (
              <React.Fragment key={org.subOrgId ?? org.subOrgName}>
                {renderRow(org)}
              </React.Fragment>
            ))
          )}
        </div>
        {legend && legend.length > 0 && <Legend items={legend} />}
      </CardBody>
    </Card>
  );
}
